import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const Profile = () => {
  const [userId, setUserId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(60);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [useOtp, setUseOtp] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddressEditable, setIsAddressEditable] = useState(true);
  const [addressAlreadySet, setAddressAlreadySet] = useState(false);
  const [emailAlreadySet, setEmailAlreadySet] = useState(false);
  const [mobileAlreadySet, setMobileAlreadySet] = useState(false);
  const [nameAlreadySet, setNameAlreadySet] = useState(false);

  // Profile fields
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    userId: "",
    sponsorId: "",
    country: "",
    Address: "",
    kycStatus: "Pending"
  });

  const [newFormData, setNewFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    address: "",
  });

  // Fetch user details on mount
  useEffect(() => {
    const UserId = localStorage.getItem("UserId");
    if (UserId) {
      setUserId(UserId);
      setFormData(prev => ({ ...prev, userId: UserId }));
      fetchUserDetails(UserId);
    } else {
      toast.error("User not found");
    }
  }, []);

  // Fetch user details API
  const fetchUserDetails = async (id) => {
    try {
      const response = await axios.post(`${API_BASE}/userdetails`, { userId: id });
      const data = response.data;

      if (data) {
        setFormData(prev => ({
          ...prev,
          name: data.name || "",
          email: data.email || "",
          mobile: data.mobile || "",
          sponsorId: data.sponserid || "",
          country: data.countrycode || "India",
          Address: data.Address || "Not Added",
          kycStatus: data.kycstatus || ""
        }));
        
        setNewFormData({
          name: data.name || "",
          email: data.email || "",
          mobile: data.mobile || "",
          address: data.Address || "",
        });
        
        // Check if user already has a name
        if (data.name && data.name.trim() !== "") {
          setNameAlreadySet(true);
        } else {
          setNameAlreadySet(false);
        }
        
        // Check if user already has an email
        if (data.email && data.email.trim() !== "") {
          setEmailAlreadySet(true);
        } else {
          setEmailAlreadySet(false);
        }
        
        // Check if user already has a mobile
        if (data.mobile && data.mobile.trim() !== "") {
          setMobileAlreadySet(true);
        } else {
          setMobileAlreadySet(false);
        }
        
        // Check if user already has an address
        if (data.Address && data.Address !== "Not Added" && data.Address.trim() !== "") {
          setIsAddressEditable(false);
          setAddressAlreadySet(true);
        } else {
          setIsAddressEditable(true);
          setAddressAlreadySet(false);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load user details");
    }
  };

  // Send OTP
  const sendOtp = async () => {
    if (!newFormData.email) {
      toast.error("Please enter a valid email.");
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await axios.post(`${API_BASE}/sendotpemail`, {
        email: newFormData.email,
        subject: "Profile Update Verification",
        type: "update-profile-otp",
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setOtpSent(true);
        setIsTimerActive(true);
        setTimer(60);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to send OTP.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Update profile WITH OTP
  const updateProfileWithOtp = async () => {
    if (!otp) {
      toast.error("Enter OTP before updating profile");
      return;
    }

    try {
      setLoading(true);
      setIsButtonDisabled(true);

      const response = await axios.post(`${API_BASE}/updateuserdetails`, {
        userId: userId,
        userName: newFormData.name,
        mobile: newFormData.mobile,
        address: newFormData.address,
        email: newFormData.email,
        otp: otp,
      });

      if (response.data.status === 1) {
        toast.success("Profile updated successfully!");
        setOtp("");
        setOtpSent(false);
        setIsTimerActive(false);
        setIsEditing(false);
        
        // After successful update, mark fields as non-editable if they were provided
        if (newFormData.name && newFormData.name.trim() !== "") {
          setNameAlreadySet(true);
        }
        
        if (newFormData.email && newFormData.email.trim() !== "") {
          setEmailAlreadySet(true);
        }
        
        if (newFormData.mobile && newFormData.mobile.trim() !== "") {
          setMobileAlreadySet(true);
        }
        
        if (newFormData.address && newFormData.address.trim() !== "") {
          setIsAddressEditable(false);
          setAddressAlreadySet(true);
        }
        
        fetchUserDetails(userId);
      } else {
        toast.error(response.data.message || "Profile update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating profile");
    } finally {
      setLoading(false);
      setIsButtonDisabled(false);
    }
  };

  // Update profile WITHOUT OTP
  const updateProfileWithoutOtp = async () => {
    try {
      setLoading(true);

      const response = await axios.post(`${API_BASE}/updateuserdetails`, {
        userId: userId,
        userName: newFormData.name,
        mobile: newFormData.mobile,
        address: newFormData.address,
        email: newFormData.email,
      });

      if (response.data.status === 1) {
        toast.success("Profile updated successfully!");
        setIsEditing(false);
        
        // After successful update, mark fields as non-editable if they were provided
        if (newFormData.name && newFormData.name.trim() !== "") {
          setNameAlreadySet(true);
        }
        
        if (newFormData.email && newFormData.email.trim() !== "") {
          setEmailAlreadySet(true);
        }
        
        if (newFormData.mobile && newFormData.mobile.trim() !== "") {
          setMobileAlreadySet(true);
        }
        
        if (newFormData.address && newFormData.address.trim() !== "") {
          setIsAddressEditable(false);
          setAddressAlreadySet(true);
        }
        
        fetchUserDetails(userId);
      } else {
        toast.error(response.data.message || "Profile update failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating profile");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent name modification if already set
    if (name === 'name' && nameAlreadySet) {
      toast.warning("Full name cannot be modified once added");
      return;
    }
    
    // Prevent email modification if already set
    if (name === 'email' && emailAlreadySet) {
      toast.warning("Email address cannot be modified once added");
      return;
    }
    
    // Prevent mobile modification if already set
    if (name === 'mobile' && mobileAlreadySet) {
      toast.warning("Mobile number cannot be modified once added");
      return;
    }
    
    // Prevent address modification if already set
    if (name === 'address' && !isAddressEditable) {
      toast.warning("Wallet address cannot be modified once added");
      return;
    }
    
    setNewFormData(prev => ({ ...prev, [name]: value }));
  };

  const enableEditing = () => {
    // Check if name is already set and show info
    if (nameAlreadySet && formData.name) {
      toast.info("Full name is already set and cannot be modified");
    }
    
    // Check if email is already set and show info
    if (emailAlreadySet && formData.email) {
      toast.info("Email address is already set and cannot be modified");
    }
    
    // Check if mobile is already set and show info
    if (mobileAlreadySet && formData.mobile) {
      toast.info("Mobile number is already set and cannot be modified");
    }
    
    // Check if address is already set and show info
    if (addressAlreadySet && formData.Address !== "Not Added") {
      toast.info("Wallet address is already set and cannot be modified");
    }
    
    setIsEditing(true);
    setNewFormData({
      name: formData.name,
      email: formData.email,
      mobile: formData.mobile,
      address: formData.Address === "Not Added" ? "" : formData.Address,
    });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setNewFormData({
      name: formData.name,
      email: formData.email,
      mobile: formData.mobile,
      address: formData.Address === "Not Added" ? "" : formData.Address,
    });
    setOtp("");
    setOtpSent(false);
    setIsTimerActive(false);
  };

  // OTP countdown
  useEffect(() => {
    if (isTimerActive && timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }
  }, [isTimerActive, timer]);

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="profile-page">
        <div className="page-header">
          <h1>Profile Settings</h1>
          <p>Manage your account information</p>
        </div>

        {/* Profile Content */}
        <div className="profile-content">
          <div className="profile-sidebar">
            <div className="profile-avatar">
              <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(formData.name)}&background=0f3d2f&color=fff&size=150`} alt="Avatar" />
              <button className="change-avatar-btn">
                <i className="fa fa-camera"></i>
              </button>
            </div>
            <div className="profile-stats">
              <div className="stat-item">
                <label>User ID</label>
                <span>{formData.userId}</span>
              </div>
              <div className="stat-item">
                <label>Sponsor ID</label>
                <span>{formData.sponsorId}</span>
              </div>
            </div>
          </div>

          <div className="profile-form">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="name"
                      value={isEditing ? newFormData.name : formData.name}
                      onChange={handleProfileChange}
                      disabled={!isEditing || nameAlreadySet}
                      placeholder={nameAlreadySet ? "Full name already set (cannot be modified)" : "Enter your full name"}
                      className={nameAlreadySet && !isEditing ? "field-disabled" : ""}
                    />
                    {nameAlreadySet && !isEditing && (
                      <div className="field-locked-badge">
                         Locked
                      </div>
                    )}
                  </div>
                  {nameAlreadySet && !isEditing && (
                    <small className="field-note">
                      <i className="fa fa-info-circle"></i> Full name can only be set once and cannot be modified for security reasons.
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <div className="input-wrapper">
                    <input
                      type="email"
                      name="email"
                      value={isEditing ? newFormData.email : formData.email}
                      onChange={handleProfileChange}
                      disabled={!isEditing || emailAlreadySet}
                      placeholder={emailAlreadySet ? "Email already set (cannot be modified)" : "Enter your email address"}
                      className={emailAlreadySet && !isEditing ? "field-disabled" : ""}
                    />
                    {emailAlreadySet && !isEditing && (
                      <div className="field-locked-badge">
                         Locked
                      </div>
                    )}
                  </div>
                  {emailAlreadySet && !isEditing && (
                    <small className="field-note">
                      <i className="fa fa-info-circle"></i> Email address can only be set once and cannot be modified for security reasons.
                    </small>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Mobile Number</label>
                  <div className="input-wrapper">
                    <input
                      type="tel"
                      name="mobile"
                      value={isEditing ? newFormData.mobile : formData.mobile}
                      onChange={handleProfileChange}
                      disabled={!isEditing || mobileAlreadySet}
                      placeholder={mobileAlreadySet ? "Mobile number already set (cannot be modified)" : "Enter your mobile number"}
                      className={mobileAlreadySet && !isEditing ? "field-disabled" : ""}
                    />
                    {mobileAlreadySet && !isEditing && (
                      <div className="field-locked-badge">
                         Locked
                      </div>
                    )}
                  </div>
                  {mobileAlreadySet && !isEditing && (
                    <small className="field-note">
                      <i className="fa fa-info-circle"></i> Mobile number can only be set once and cannot be modified for security reasons.
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    disabled={true}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>USDT (BEP20) Wallet Address</label>
                <div className="address-input-wrapper">
                  <input
                    type="text"
                    name="address"
                    value={isEditing ? newFormData.address : formData.Address}
                    onChange={handleProfileChange}
                    disabled={!isEditing || !isAddressEditable}
                    placeholder={addressAlreadySet ? "Wallet address already set (cannot be modified)" : "Enter your USDT (BEP20) wallet address"}
                    className={addressAlreadySet && !isEditing ? "address-disabled" : ""}
                  />
                  {addressAlreadySet && !isEditing && (
                    <div className="address-locked-badge">
                      <i className="fa fa-lock"></i> Address Locked
                    </div>
                  )}
                  {!isEditing && !addressAlreadySet && formData.Address === "Not Added" && (
                    <button 
                      type="button" 
                      className="btn-add-address"
                      onClick={enableEditing}
                    >
                      <i className="fa fa-plus"></i> Add Wallet Address
                    </button>
                  )}
                </div>
                {addressAlreadySet && !isEditing && (
                  <small className="address-note">
                    <i className="fa fa-info-circle"></i> Wallet address can only be added once and cannot be modified for security reasons.
                  </small>
                )}
              </div>

              <div className="form-actions">
                {!isEditing ? (
                  <button type="button" className="btn-primary" onClick={enableEditing}>
                    <i className="fa fa-edit"></i> Edit Profile
                  </button>
                ) : (
                  <>
                    {useOtp ? (
                      otpSent ? (
                        <div style={{ width: '100%' }}>
                          <div className="form-group">
                            <label>Enter OTP</label>
                            <input 
                              type="text" 
                              value={otp} 
                              onChange={(e) => setOtp(e.target.value)} 
                              placeholder="Enter 6-digit OTP"
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button type="button" className="btn-success" onClick={updateProfileWithOtp} disabled={loading || isButtonDisabled}>
                              {loading ? "Verifying..." : "Verify & Update"}
                            </button>
                            <button type="button" className="btn-secondary" onClick={cancelEditing}>
                              Cancel
                            </button>
                          </div>
                          {isTimerActive ? (
                            <p className="otp-timer">Resend in {timer}s</p>
                          ) : (
                            <button type="button" className="btn-link" onClick={sendOtp}>
                              Resend OTP
                            </button>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button type="button" className="btn-primary" onClick={sendOtp} disabled={isSendingOtp}>
                            {isSendingOtp ? "Sending OTP..." : "Send OTP to Update"}
                          </button>
                          <button type="button" className="btn-secondary" onClick={cancelEditing}>
                            Cancel
                          </button>
                        </div>
                      )
                    ) : (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="button" className="btn-success" onClick={updateProfileWithoutOtp} disabled={loading}>
                          {loading ? "Updating..." : "Save Changes"}
                        </button>
                        <button type="button" className="btn-secondary" onClick={cancelEditing}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;